import React, { useState, useMemo } from "react";
import {
  Text, Table, Thead, Tbody, Tr, Th, Td, HStack, IconButton, Box, Badge,
  Flex, Input, InputGroup, InputLeftElement, Select,
} from "@chakra-ui/react";
import { FaSync, FaChevronRight, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { HiMagnifyingGlass } from "react-icons/hi2";
import { RangeDatepicker } from "chakra-dayzed-datepicker";
import { Card } from "components/Card";
import { H2, H3 } from "components/text/headings";
import { Page } from "components/Page";
import { Paginator } from "components/Paginator";
import { WithAuthorization } from "context";
import { useAuth } from "context";
import { useQuery, useQueryClient } from "react-query";
import { useAsyncDebounce } from "utils";
import {
  getUsage, getEmbeddingUsage, getUsageByContext, getContextComponents, getContextCalls,
  getUsageLabels,
} from "api/ai/ai.service";
import { sortContexts, nextSortDirection, SORT_DIRECTIONS } from "./sortContexts";

const PILL_SELECT_STYLES = {
  borderRadius: "full",
  border: "1px solid",
  borderColor: "gray.200",
  bg: "white",
  fontSize: "body.s",
  fontWeight: "500",
  color: "gray.700",
  h: "36px",
  cursor: "pointer",
  _hover: { borderColor: "gray.400" },
  _focus: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
};

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];

function UsageViewer() {
  const { getAccessTokenSilently } = useAuth();
  const queryClient = useQueryClient();
  const [selectedContext, setSelectedContext] = useState(null); // {context_type, context_id}

  // Toolbar state — client-side (context type, search, sort, pagination) vs server-side
  // (date range, round, component, model — passed into getUsageByContext as query-key deps).
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSetSearch = useAsyncDebounce((value) => setSearchQuery(value), 400);
  const [contextTypeFilter, setContextTypeFilter] = useState("");
  const [roundFilter, setRoundFilter] = useState("");
  const [componentFilter, setComponentFilter] = useState("");
  const [modelFilter, setModelFilter] = useState("");
  const [dateRange, setDateRange] = useState([]); // [] | [Date] | [Date, Date]
  /*
    Default to newest first. `first_call` rather than `last_call` because the Date column renders
    first_call — defaulting to the other one would sort the table by a value that is not on screen, so the
    Date column would look shuffled and the sort arrow would sit on a different header than the ordering.
  */
  const [sortColumn, setSortColumn] = useState("first_call");
  const [sortDirection, setSortDirection] = useState(SORT_DIRECTIONS.DESC);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSetSearch(value);
    setPageIndex(0);
  };

  const handleSort = (column) => {
    if (column === sortColumn) {
      setSortDirection(nextSortDirection(sortDirection));
    } else {
      setSortColumn(column);
      setSortDirection(SORT_DIRECTIONS.ASC);
    }
  };

  const serverFilterParams = {
    dateFrom: dateRange[0] ? dateRange[0].toISOString() : undefined,
    dateTo: dateRange[1] ? dateRange[1].toISOString() : undefined,
    roundId: roundFilter || undefined,
    component: componentFilter || undefined,
    modelName: modelFilter || undefined,
  };

  const { data: usage, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["aiUsage"],
    queryFn: () => getAccessTokenSilently().then((token) => getUsage({}, token)),
  });

  const {
    data: contextUsage,
    isLoading: isLoadingContexts,
    isFetching: isFetchingContexts,
    refetch: refetchContexts,
  } = useQuery({
    queryKey: ["aiUsageContexts", serverFilterParams],
    queryFn: () =>
      getAccessTokenSilently().then((token) => getUsageByContext(serverFilterParams, token)),
  });

  const { data: componentUsage, isLoading: isLoadingComponents } = useQuery({
    queryKey: ["aiContextComponents", selectedContext],
    queryFn: () =>
      getAccessTokenSilently().then((token) =>
        getContextComponents(selectedContext.context_type, selectedContext.context_id, {}, token)
      ),
    enabled: !!selectedContext,
  });

  const { data: callUsage, isLoading: isLoadingCalls } = useQuery({
    queryKey: ["aiContextCalls", selectedContext],
    queryFn: () =>
      getAccessTokenSilently().then((token) =>
        getContextCalls(selectedContext.context_type, selectedContext.context_id, {}, token)
      ),
    enabled: !!selectedContext,
  });

  // Distinct question/round/report-writer-task ids present in the current (already
  // server-filtered) result — resolved to human-readable labels via one batch POST, so
  // Question/Round/report-writer Task rows show real titles/names instead of a raw-UUID
  // prefix. Falls back gracefully (see getContextLabel) for anything that fails to resolve
  // (historical/unsynced Java-side data).
  const labelIds = useMemo(() => {
    const questionIds = new Set();
    const roundIds = new Set();
    const reportTaskIds = new Set();
    (contextUsage || []).forEach((c) => {
      if (c.context_type === "question" && c.context_id) questionIds.add(c.context_id);
      if (c.round_id) roundIds.add(c.round_id);
      if (c.context_type === "task" && c.task_type === "report_writer" && c.context_id) {
        reportTaskIds.add(c.context_id);
      }
    });
    return {
      questionIds: Array.from(questionIds).sort(),
      roundIds: Array.from(roundIds).sort(),
      reportTaskIds: Array.from(reportTaskIds).sort(),
    };
  }, [contextUsage]);

  const { data: usageLabels } = useQuery({
    queryKey: ["aiUsageLabels", labelIds],
    queryFn: () =>
      getAccessTokenSilently().then((token) => getUsageLabels(labelIds, token)),
    enabled:
      labelIds.questionIds.length > 0 ||
      labelIds.roundIds.length > 0 ||
      labelIds.reportTaskIds.length > 0,
  });

  // Round filter options — contextUsage rows carry a representative round_id (Part 2B).
  const distinctRounds = useMemo(() => {
    const ids = new Set((contextUsage || []).map((c) => c.round_id).filter(Boolean));
    return Array.from(ids).sort();
  }, [contextUsage]);

  // Component/model filter options — contextUsage is aggregated PAST the component/model
  // level (one row per context, not per component), so pull these from `usage` instead: the
  // org-wide component+model rollup this page already fetches for the lifetime totals.
  const distinctComponents = useMemo(() => {
    const values = new Set((usage || []).map((r) => r.component).filter(Boolean));
    return Array.from(values).sort();
  }, [usage]);
  const distinctModels = useMemo(() => {
    const values = new Set((usage || []).map((r) => r.model_name).filter(Boolean));
    return Array.from(values).sort();
  }, [usage]);

  if (isLoading || isLoadingContexts) return null;

  const totalCost = (usage || []).reduce((sum, row) => sum + parseFloat(row.cost || 0), 0);
  const totalTokens = (usage || []).reduce((sum, row) => sum + (row.input_tokens || 0) + (row.output_tokens || 0), 0);

  const handleContextClick = (context) => {
    if (selectedContext?.context_type === context.context_type && selectedContext?.context_id === context.context_id) {
      setSelectedContext(null); // toggle off
    } else {
      setSelectedContext({ context_type: context.context_type, context_id: context.context_id });
    }
  };

  const getContextLabel = (context) => {
    const id = context.context_id || "";
    const shortId = id.substring(0, 8);
    // Real title when resolved (question text via getUsageLabels); graceful UUID8 fallback
    // otherwise — covers historical/unsynced Java-side data or a resolution failure.
    const resolvedTitle = context.context_type === "question" ? usageLabels?.questions?.[id] : null;
    // Report-writer tasks: show the actual report doc title instead of a raw task id.
    const resolvedReportTitle =
      context.context_type === "task" && context.task_type === "report_writer"
        ? usageLabels?.reports?.[id]
        : null;

    switch (context.context_type) {
      case "task":
        return {
          label: resolvedReportTitle || `Task ${shortId}`,
          badge: "Task",
          badgeColor: "purple",
        };
      case "question":
        return { label: resolvedTitle || `Question ${shortId}`, badge: "Question", badgeColor: "blue" };
      case "report":
        return { label: `Report ${shortId}`, badge: "Report", badgeColor: "green" };
      case "interactive":
        return { label: `Chat ${shortId}`, badge: "Interactive", badgeColor: "gray" };
      default:
        return { label: shortId, badge: "Unknown", badgeColor: "gray" };
    }
  };

  const getRoundLabel = (roundId) => {
    if (!roundId) return "—";
    return usageLabels?.rounds?.[roundId] || `${roundId.substring(0, 8)}`;
  };

  const formatDuration = (firstCall, lastCall) => {
    if (!firstCall || !lastCall) return "—";
    const start = new Date(firstCall);
    const end = new Date(lastCall);
    const diffMs = end - start;
    const diffMins = Math.round(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}min`;
    const diffHours = Math.round(diffMins / 60);
    return `${diffHours}h`;
  };

  /**
   * Date and time of day, on one line.
   *
   * Both, because usage is often several contexts within one day: a date alone cannot be read as an
   * ordering, so a table sorted newest-first looked arbitrary within today's rows. Seconds are dropped —
   * they add width to every row and answer nothing anyone asks of this page.
   */
  const formatDateTime = (value) => {
    if (!value) return "—";
    const at = new Date(value);
    if (Number.isNaN(at.getTime())) return "—";
    return `${at.toLocaleDateString()}, ${at.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  };

  const formatCallTime = (createdDate) => {
    if (!createdDate) return "—";
    return new Date(createdDate).toLocaleTimeString();
  };

  const handleRefresh = () => {
    refetch();
    refetchContexts();
    queryClient.invalidateQueries(["aiUsageLabels"]);
    if (selectedContext) {
      queryClient.invalidateQueries(["aiContextComponents", selectedContext]);
      queryClient.invalidateQueries(["aiContextCalls", selectedContext]);
    }
  };

  // Client-side pipeline: context-type filter -> free-text search -> sort -> paginate.
  // Runs over contextUsage, which is already narrowed server-side by date/round/component/model.
  const filteredByType = (contextUsage || []).filter(
    (c) => !contextTypeFilter || c.context_type === contextTypeFilter
  );

  const rowsWithLabel = filteredByType.map((c) => ({ ...c, _label: getContextLabel(c).label }));

  const searched = searchQuery
    ? rowsWithLabel.filter((c) => {
        const haystack = [c._label, c.context_type, c.task_type, getRoundLabel(c.round_id)]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(searchQuery.toLowerCase());
      })
    : rowsWithLabel;

  const sorted = sortContexts(searched, sortColumn, sortDirection);

  const totalFilteredCount = sorted.length;
  const numberOfPages = Math.max(1, Math.ceil(totalFilteredCount / pageSize));
  const safePageIndex = Math.min(pageIndex, numberOfPages - 1);
  const pageRows = sorted.slice(safePageIndex * pageSize, safePageIndex * pageSize + pageSize);

  const sortIcon = (column) => {
    if (sortColumn !== column) return <FaSort />;
    if (sortDirection === SORT_DIRECTIONS.ASC) return <FaSortUp />;
    if (sortDirection === SORT_DIRECTIONS.DESC) return <FaSortDown />;
    return <FaSort />;
  };

  const SortableTh = ({ column, children, isNumeric }) => (
    <Th isNumeric={isNumeric}>
      <Flex align="center" gap={1} onClick={() => handleSort(column)} cursor="pointer" justify={isNumeric ? "flex-end" : "flex-start"}>
        <Box>{children}</Box>
        <Box>{sortIcon(column)}</Box>
      </Flex>
    </Th>
  );

  return (
    <Card mb={4}>
      <HStack justify="space-between" mb={0}>
        <H2 mb={0}>LLM Usage by Context</H2>
        <IconButton
          size="sm"
          variant="ghost"
          icon={<FaSync />}
          aria-label="Refresh"
          isLoading={isFetching || isFetchingContexts}
          onClick={handleRefresh}
        />
      </HStack>
      <Text fontSize="sm" color="gray.500" mb={3}>
        Lifetime totals: {totalTokens.toLocaleString()} tokens · ${totalCost.toFixed(4)} USD
      </Text>

      <Flex gap={3} wrap="wrap" mb={3} align="center">
        <InputGroup size="sm" w="240px">
          <InputLeftElement pointerEvents="none">
            <HiMagnifyingGlass color="gray" />
          </InputLeftElement>
          <Input
            placeholder="Search context, type, round..."
            value={searchInput}
            onChange={handleSearchChange}
            borderRadius="full"
          />
        </InputGroup>
        <Select
          {...PILL_SELECT_STYLES}
          w="auto"
          minW="140px"
          value={contextTypeFilter}
          onChange={(e) => { setContextTypeFilter(e.target.value); setPageIndex(0); }}
        >
          <option value="">All types</option>
          <option value="task">Task</option>
          <option value="question">Question</option>
          <option value="report">Report</option>
          <option value="interactive">Chat</option>
        </Select>
        <Select
          {...PILL_SELECT_STYLES}
          w="auto"
          minW="140px"
          value={roundFilter}
          onChange={(e) => { setRoundFilter(e.target.value); setPageIndex(0); }}
        >
          <option value="">All rounds</option>
          {distinctRounds.map((rid) => (
            <option key={rid} value={rid}>
              {getRoundLabel(rid)}
            </option>
          ))}
        </Select>
        <Select
          {...PILL_SELECT_STYLES}
          w="auto"
          minW="140px"
          value={componentFilter}
          onChange={(e) => { setComponentFilter(e.target.value); setPageIndex(0); }}
        >
          <option value="">All components</option>
          {distinctComponents.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </Select>
        <Select
          {...PILL_SELECT_STYLES}
          w="auto"
          minW="140px"
          value={modelFilter}
          onChange={(e) => { setModelFilter(e.target.value); setPageIndex(0); }}
        >
          <option value="">All models</option>
          {distinctModels.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </Select>
        <RangeDatepicker
          selectedDates={dateRange}
          onDateChange={(dates) => { setDateRange(dates); setPageIndex(0); }}
          configs={{ dateFormat: "d MMM yyyy" }}
          propsConfigs={{ inputProps: { size: "sm", borderRadius: "full", placeholder: "Date range" } }}
        />
      </Flex>

      {!contextUsage?.length ? (
        <Text fontSize="sm" color="gray.500">
          No LLM usage recorded yet.
        </Text>
      ) : !pageRows.length ? (
        <Text fontSize="sm" color="gray.500">
          No usage matches the current filters.
        </Text>
      ) : (
        <>
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th></Th>
              <SortableTh column="label">Context</SortableTh>
              <SortableTh column="context_type">Type</SortableTh>
              <SortableTh column="round_id">Round</SortableTh>
              <SortableTh column="thread_count" isNumeric>Threads</SortableTh>
              <SortableTh column="calls" isNumeric>Calls</SortableTh>
              <SortableTh column="total_tokens" isNumeric>Tokens</SortableTh>
              <SortableTh column="cost" isNumeric>Cost</SortableTh>
              <SortableTh column="errors" isNumeric>Errors</SortableTh>
              <SortableTh column="first_call">Date</SortableTh>
              <SortableTh column="last_call">Duration</SortableTh>
            </Tr>
          </Thead>
          <Tbody>
            {pageRows.map((context, i) => {
              const isSelected =
                selectedContext?.context_type === context.context_type &&
                selectedContext?.context_id === context.context_id;
              const { label, badge, badgeColor } = getContextLabel(context);

              return (
                <React.Fragment key={i}>
                  <Tr
                    onClick={() => handleContextClick(context)}
                    cursor="pointer"
                    _hover={{ bg: "gray.50" }}
                    bg={isSelected ? "blue.50" : undefined}
                  >
                    <Td>
                      <IconButton
                        size="xs"
                        variant="ghost"
                        icon={<FaChevronRight />}
                        aria-label="View components"
                        transform={isSelected ? "rotate(90deg)" : undefined}
                        transition="transform 0.2s"
                      />
                    </Td>
                    <Td fontFamily="mono" fontSize="xs">
                      {label}
                    </Td>
                    <Td>
                      <Badge colorScheme={badgeColor}>{badge}</Badge>
                      {context.task_type && (
                        <Text fontSize="xs" color="gray.500">
                          {context.task_type}
                        </Text>
                      )}
                    </Td>
                    <Td fontSize="xs">{getRoundLabel(context.round_id)}</Td>
                    <Td isNumeric>{context.thread_count || 0}</Td>
                    <Td isNumeric>{context.calls || 0}</Td>
                    <Td isNumeric>{(context.total_tokens || 0).toLocaleString()}</Td>
                    <Td isNumeric>${parseFloat(context.cost || 0).toFixed(4)}</Td>
                    <Td
                      isNumeric
                      color={context.errors ? "red.500" : undefined}
                      fontWeight={context.errors ? "bold" : undefined}
                    >
                      {context.errors || 0}
                    </Td>
                    <Td fontSize="xs" whiteSpace="nowrap">{formatDateTime(context.first_call)}</Td>
                    <Td fontSize="xs">{formatDuration(context.first_call, context.last_call)}</Td>
                  </Tr>

                  {isSelected && (
                    <Tr>
                      <Td colSpan={11} p={0}>
                        <Box mt={2} mb={2} ml={8} mr={2} p={3} bg="gray.50" borderRadius="md">
                          <H3 mb={2} mt={0} pt={0}>
                            Component Breakdown
                          </H3>
                          {isLoadingComponents ? (
                            <Text fontSize="sm" color="gray.500">
                              Loading components...
                            </Text>
                          ) : !componentUsage?.length ? (
                            <Text fontSize="sm" color="gray.500">
                              No component data.
                            </Text>
                          ) : (
                            <Table size="sm" variant="simple" bg="white">
                              <Thead>
                                <Tr>
                                  <Th>Component</Th>
                                  <Th>Model</Th>
                                  <Th isNumeric>Calls</Th>
                                  <Th isNumeric>Input</Th>
                                  <Th isNumeric>Output</Th>
                                  <Th isNumeric>Cost</Th>
                                  <Th isNumeric>Errors</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {componentUsage.map((comp, j) => (
                                  <Tr key={j}>
                                    <Td>{comp.component || "—"}</Td>
                                    <Td>{comp.model_name || "—"}</Td>
                                    <Td isNumeric>{comp.calls || 0}</Td>
                                    <Td isNumeric>{(comp.input_tokens || 0).toLocaleString()}</Td>
                                    <Td isNumeric>{(comp.output_tokens || 0).toLocaleString()}</Td>
                                    <Td isNumeric>${parseFloat(comp.cost || 0).toFixed(4)}</Td>
                                    <Td
                                      isNumeric
                                      color={comp.errors ? "red.500" : undefined}
                                      fontWeight={comp.errors ? "bold" : undefined}
                                    >
                                      {comp.errors || 0}
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          )}

                          <H3 mb={2} mt={4}>
                            Call Timeline
                          </H3>
                          {isLoadingCalls ? (
                            <Text fontSize="sm" color="gray.500">
                              Loading calls...
                            </Text>
                          ) : !callUsage?.length ? (
                            <Text fontSize="sm" color="gray.500">
                              No call data.
                            </Text>
                          ) : (
                            <Table size="sm" variant="simple" bg="white">
                              <Thead>
                                <Tr>
                                  <Th>Time</Th>
                                  <Th isNumeric>Step</Th>
                                  <Th>Component</Th>
                                  <Th>Model</Th>
                                  <Th isNumeric>Input</Th>
                                  <Th isNumeric>Output</Th>
                                  <Th isNumeric>Tool Rounds</Th>
                                  <Th isNumeric>Cost</Th>
                                  <Th>Error</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {callUsage.map((call, k) => (
                                  <Tr key={k}>
                                    <Td fontSize="xs">{formatCallTime(call.created_date)}</Td>
                                    <Td isNumeric>{call.step_index ?? "—"}</Td>
                                    <Td>{call.component || "—"}</Td>
                                    <Td>{call.model_name || "—"}</Td>
                                    <Td isNumeric>{(call.input_tokens || 0).toLocaleString()}</Td>
                                    <Td isNumeric>{(call.output_tokens || 0).toLocaleString()}</Td>
                                    <Td isNumeric>{call.tool_rounds || 0}</Td>
                                    <Td isNumeric>${parseFloat(call.cost || 0).toFixed(4)}</Td>
                                    <Td fontSize="xs" color={call.error ? "red.500" : undefined}>
                                      {call.error || "—"}
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          )}
                        </Box>
                      </Td>
                    </Tr>
                  )}
                </React.Fragment>
              );
            })}
          </Tbody>
        </Table>
        <Flex justify="center" mt={4}>
          <Paginator
            selectedPage={safePageIndex + 1}
            numberOfPages={numberOfPages}
            handlePageSelection={(newPage) => setPageIndex(newPage - 1)}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={(newSize) => { setPageSize(newSize); setPageIndex(0); }}
            totalCount={totalFilteredCount}
          />
        </Flex>
        </>
      )}
    </Card>
  );
}

function EmbeddingUsageViewer() {
  const { getAccessTokenSilently } = useAuth();
  const { data: usage, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["aiEmbeddingUsage"],
    queryFn: () => getAccessTokenSilently().then((token) => getEmbeddingUsage({}, token)),
  });

  if (isLoading) return null;

  const totalCost = (usage || []).reduce((sum, row) => sum + parseFloat(row.cost || 0), 0);
  const totalTokens = (usage || []).reduce((sum, row) => sum + (row.tokens || 0), 0);

  return (
    <Card mb={4}>
      <HStack justify="space-between" mb={0}>
        <H2 mb={0}>Embedding Usage</H2>
        <IconButton
          size="sm"
          variant="ghost"
          icon={<FaSync />}
          aria-label="Refresh"
          isLoading={isFetching}
          onClick={() => refetch()}
        />
      </HStack>
      <Text fontSize="sm" color="gray.500" mb={3}>
        Lifetime totals: {totalTokens.toLocaleString()} tokens · ${totalCost.toFixed(4)} USD
      </Text>
      {!usage?.length ? (
        <Text fontSize="sm" color="gray.500">
          No embedding usage recorded yet.
        </Text>
      ) : (
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Component</Th>
              <Th>Model</Th>
              <Th isNumeric>Calls</Th>
              <Th isNumeric>Tokens</Th>
              <Th isNumeric>Cost (USD)</Th>
            </Tr>
          </Thead>
          <Tbody>
            {usage.map((row, i) => (
              <Tr key={i}>
                <Td>{row.component || "—"}</Td>
                <Td>{row.model || "—"}</Td>
                <Td isNumeric>{row.calls}</Td>
                <Td isNumeric>{(row.tokens || 0).toLocaleString()}</Td>
                <Td isNumeric>${parseFloat(row.cost || 0).toFixed(4)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </Card>
  );
}

function AIUsage({ componentRequiredPermissions }) {
  return (
    <Page title="AI Usage" componentRequiredPermissions={componentRequiredPermissions}>
      <UsageViewer />
      <EmbeddingUsageViewer />
    </Page>
  );
}

export default WithAuthorization(AIUsage);
